import React from "react";
import { createPortal } from "react-dom";
import { X, Sun, Moon, Palette } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useTheme } from "../context/ThemeContext";

interface ThemeModalProps {
    show: boolean;
    onClose: () => void;
}

const ThemeModal: React.FC<ThemeModalProps> = ({ show, onClose }) => {
    const { t } = useTranslation();
    const { mode, colors, setMode, setColors } = useTheme();

    if (!show) return null;

    const handleModeSelect = (newMode: 'light' | 'dark') => {
        setMode(newMode);
    };

    const handleColorChange = (colorType: 'primary' | 'secondary' | 'tertiary', value: string) => {
        setColors({
            ...colors,
            [colorType]: value,
        });
    };

    const colorLabels = {
        primary: t("sidebarFooter.primaryColor") || "Primary Color",
        secondary: t("sidebarFooter.secondaryColor") || "Secondary Color",
        tertiary: t("sidebarFooter.tertiaryColor") || "Third Color",
    };

    return createPortal(
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-[9999] backdrop-blur-sm">
            <div className="bg-white dark:bg-gray-800 p-8 rounded-2xl shadow-2xl w-[500px] max-w-[90vw] border border-gray-200 dark:border-gray-700 max-h-[90vh] overflow-y-auto">
                <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                        {t("sidebarFooter.theme")}
                    </h2>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                    >
                        <X size={20} />
                    </button>
                </div>
                
                {/* Mode Selection */}
                <div className="mb-8">
                    <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                        {t("sidebarFooter.mode") || "Mode"}
                    </h3>
                    <div className="space-y-3">
                        <button
                            onClick={() => handleModeSelect('light')}
                            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg border-2 transition-all duration-200 ${
                                mode === 'light'
                                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400'
                                    : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300'
                            }`}
                        >
                            <Sun size={20} className={mode === 'light' ? 'text-blue-600 dark:text-blue-400' : 'text-gray-500 dark:text-gray-400'} />
                            <span className="font-medium">{t("sidebarFooter.lightMode")}</span>
                            {mode === 'light' && (
                                <span className="ml-auto text-blue-600 dark:text-blue-400">✓</span>
                            )}
                        </button>

                        <button
                            onClick={() => handleModeSelect('dark')}
                            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg border-2 transition-all duration-200 ${
                                mode === 'dark'
                                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400'
                                    : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300'
                            }`}
                        >
                            <Moon size={20} className={mode === 'dark' ? 'text-blue-600 dark:text-blue-400' : 'text-gray-500 dark:text-gray-400'} />
                            <span className="font-medium">{t("sidebarFooter.darkMode")}</span>
                            {mode === 'dark' && (
                                <span className="ml-auto text-blue-600 dark:text-blue-400">✓</span>
                            )}
                        </button>
                    </div>
                </div>

                {/* Color Selection */}
                <div>
                    <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
                        <Palette size={16} />
                        {t("sidebarFooter.colors") || "Colors"}
                    </h3>
                    <div className="space-y-4">
                        {/* Primary Color */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                {colorLabels.primary}
                            </label>
                            <div className="flex items-center gap-3">
                                <input
                                    type="color"
                                    value={colors.primary}
                                    onChange={(e) => handleColorChange('primary', e.target.value)}
                                    className="w-16 h-10 rounded cursor-pointer border border-gray-300 dark:border-gray-600"
                                />
                                <input
                                    type="text"
                                    value={colors.primary}
                                    onChange={(e) => handleColorChange('primary', e.target.value)}
                                    className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    placeholder="#0f766e"
                                />
                            </div>
                        </div>

                        {/* Secondary Color */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                {colorLabels.secondary}
                            </label>
                            <div className="flex items-center gap-3">
                                <input
                                    type="color"
                                    value={colors.secondary}
                                    onChange={(e) => handleColorChange('secondary', e.target.value)}
                                    className="w-16 h-10 rounded cursor-pointer border border-gray-300 dark:border-gray-600"
                                />
                                <input
                                    type="text"
                                    value={colors.secondary}
                                    onChange={(e) => handleColorChange('secondary', e.target.value)}
                                    className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    placeholder="#0891b2"
                                />
                            </div>
                        </div>

                        {/* Tertiary Color */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                {colorLabels.tertiary}
                            </label>
                            <div className="flex items-center gap-3">
                                <input
                                    type="color"
                                    value={colors.tertiary}
                                    onChange={(e) => handleColorChange('tertiary', e.target.value)}
                                    className="w-16 h-10 rounded cursor-pointer border border-gray-300 dark:border-gray-600"
                                />
                                <input
                                    type="text"
                                    value={colors.tertiary}
                                    onChange={(e) => handleColorChange('tertiary', e.target.value)}
                                    className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    placeholder="#0e7490"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>,
        document.body
    );
};

export default ThemeModal;
